/**
 * HarnClient — fetch wrapper for lexruntime harn-service HTTP API.
 *
 * LexIDE has exactly one harn-service endpoint per session (the user's
 * lexruntime instance), so the client is constructed with a single
 * `{ baseUrl, getToken }` target. This is the single-instance
 * counterpart to LexPortal's multi-runtime `HarnTarget` discriminated
 * union — LexPortal admins can switch between runtimes; LexIDE users
 * cannot.
 *
 * Two wiring modes for `baseUrl`:
 * - **Gateway** (prod + dev-against-gateway): pass the runtime's
 *   `/harn` base URL (e.g. `"/harn-gateway-base/harn"` via the Vite
 *   proxy, or `"https://runtime.lexiun.io/harn"` in prod). `getToken`
 *   returns the Lexiun JWT; the client sends `Authorization: Bearer …`.
 *   WS streaming is NOT supported in gateway mode — the openclaw plugin
 *   proxy returns 501 on upgrade (per [proxy.ts:76–86](../../../lexruntime/openclaw/extensions/lexiun/src/harnesses/proxy.ts)).
 *   Use polling.
 * - **Direct** (harness dev loop only): pass the Vite proxy prefix
 *   `"/harn-direct"`. `getToken` can return `null` — direct mode trusts
 *   any caller on :3210. WS streaming works via `/harnws-direct`.
 *
 * Wire contract: see `lexruntime/harn-service/harnesses/ai-docs/consumer-guide.md`
 * (canonical) and the Zod schemas at `@lexiun/harn-sdk/protocol`.
 */

import type {
  HarnessManifestSummary,
  HarnHealth,
  HarnTaskRecord,
  HarnTaskResult,
  HarnTaskSummary,
  HarnWireFrame,
  ListTasksParams,
  RunHarnessBody,
  RunHarnessResponse,
} from "@/types/harn";

export class HarnApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`HarnApiError ${status}: ${body.slice(0, 200)}`);
    this.name = "HarnApiError";
  }
}

/**
 * Distinguishes `GET /tasks/:id/result` HTTP 409 (task still running —
 * keep polling) from real errors. Per
 * `consumer-guide.md §10`, `pending` is a REST-only code; it never
 * appears as a WS `error` frame. Callers that poll the result endpoint
 * should catch `HarnTaskPendingError` and keep polling rather than
 * surfacing it to the user.
 */
export class HarnTaskPendingError extends HarnApiError {
  constructor(body: string) {
    super(409, body);
    this.name = "HarnTaskPendingError";
  }
}

/**
 * LexIDE's single-instance shape. `getToken` returns the Lexiun JWT in
 * gateway mode; may return `null` in direct (unauthenticated) mode.
 */
export interface HarnClientTarget {
  /**
   * Path or URL prefix prepended to every HTTP request.
   * Gateway-mode example: `"/harn-gateway-base/harn"` (dev) or
   * `"https://runtime.example/harn"` (prod).
   * Direct-mode example: `"/harn-direct"`.
   */
  baseUrl: string;
  /**
   * Called once per request to obtain a Lexiun JWT. Return `null` in
   * direct mode (harn-service trusts localhost). The Step-0a shared
   * helper `getToken` from `@/lib/lexiun-jwt` satisfies this signature.
   */
  getToken: () => Promise<string | null>;
  /**
   * `"gateway"` disables WS streaming (plugin proxy returns 501 on
   * upgrade). `"direct"` enables it via the Vite `/harnws-direct`
   * passthrough. Defaults to `"gateway"` when absent.
   */
  mode?: "gateway" | "direct";
}

export class HarnClient {
  constructor(public readonly target: HarnClientTarget) {}

  get modeLabel(): "Gateway (runtime)" | "Direct (host)" {
    return this.target.mode === "direct" ? "Direct (host)" : "Gateway (runtime)";
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";

    const token = await this.target.getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const url = `${this.target.baseUrl}${path}`;
    const res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const text = await res.text();
    if (!res.ok) {
      // 409 on `/tasks/:id/result` means the task is still running —
      // the caller should keep polling, not surface the error. Per
      // consumer-guide.md §10.
      if (res.status === 409 && path.endsWith("/result")) {
        throw new HarnTaskPendingError(text);
      }
      throw new HarnApiError(res.status, text);
    }
    // harn-service always returns JSON; empty bodies are possible on
    // some errors we already branched on above.
    return text ? (JSON.parse(text) as T) : ({} as T);
  }

  // ── Health ─────────────────────────────────────────────────────

  async getHealth(): Promise<HarnHealth> {
    return this.request("GET", "/health");
  }

  // ── Harness catalogue ──────────────────────────────────────────

  async listHarnesses(): Promise<{ harnesses: HarnessManifestSummary[] }> {
    return this.request("GET", "/harnesses");
  }

  async getHarness(id: string): Promise<Record<string, unknown>> {
    return this.request("GET", `/harnesses/${encodeURIComponent(id)}`);
  }

  // ── Tasks ──────────────────────────────────────────────────────

  async listTasks(params?: ListTasksParams): Promise<{ tasks: HarnTaskSummary[] }> {
    const qs = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== "") qs.set(k, String(v));
      }
    }
    const q = qs.toString();
    return this.request("GET", `/tasks${q ? `?${q}` : ""}`);
  }

  async getTask(taskId: string): Promise<HarnTaskRecord> {
    return this.request("GET", `/tasks/${encodeURIComponent(taskId)}`);
  }

  /**
   * Fetch the terminal result for a task.
   * Throws `HarnTaskPendingError` (HTTP 409) while the task is still
   * running — callers polling the endpoint must catch and retry.
   */
  async getTaskResult(taskId: string): Promise<HarnTaskResult> {
    return this.request("GET", `/tasks/${encodeURIComponent(taskId)}/result`);
  }

  async cancelTask(taskId: string): Promise<{ cancelled: true }> {
    return this.request("DELETE", `/tasks/${encodeURIComponent(taskId)}`);
  }

  /**
   * Enqueue a task. Accepts an optional `resume` payload that carries a
   * previous `awaiting_user` terminal's token + answers.
   */
  async runHarness(
    harnessId: string,
    body: RunHarnessBody,
  ): Promise<RunHarnessResponse> {
    return this.request("POST", `/run/${encodeURIComponent(harnessId)}`, body);
  }

  /**
   * Continue an `awaiting_user` chain by POSTing the user's answers
   * back to the same harness. `token` comes from the prior
   * `awaiting_user` terminal (WS frame or REST result); `answers` is
   * one `{question_id, value}` per question.
   *
   * Mirrors consumer-guide.md §10 step 3: `parent_task_id` is derived
   * server-side from the token, so we don't forward it. Topology
   * (orgId/clientId/matterId/caseId) must match the parent task — the
   * server rejects topology mismatches with `resume_invalid`.
   */
  async resumeHarness(
    harnessId: string,
    topology: Pick<RunHarnessBody, "orgId" | "clientId" | "matterId" | "caseId">,
    token: string,
    answers: Array<{ question_id: string; value: unknown }>,
  ): Promise<RunHarnessResponse> {
    return this.runHarness(harnessId, {
      ...topology,
      // `input` isn't used by the runtime on a resume POST (the harness
      // reads its chain state from the stored awaiting_user record),
      // but the envelope validator requires SOMETHING — `{}` is safe.
      input: {},
      resume: { token, answers },
    });
  }

  // ── WebSocket frame streaming ──────────────────────────────────

  /**
   * Whether this client supports live WS frame streaming.
   * Direct mode supports it (Vite `/harnws-direct` forwards `ws://`);
   * gateway mode does not — the openclaw plugin proxy returns 501 on
   * upgrade (see `lexruntime/openclaw/extensions/lexiun/src/harnesses/proxy.ts`).
   */
  get streamingSupported(): boolean {
    return this.target.mode === "direct";
  }

  /**
   * Subscribe to live frames for a task. Returns an unsubscribe
   * function. Frames: `progress`, `log`, `llm_stream`, and terminals
   * (`result` / `error` / `awaiting_user`). The socket closes after
   * the terminal frame.
   *
   * In direct mode the browser connects via the `/harnws-direct` Vite
   * proxy which forwards to `ws://127.0.0.1:3210/ws/tasks/:id`.
   *
   * In gateway mode this throws — callers should fall back to polling
   * `GET /tasks/:id` + `GET /tasks/:id/result`.
   */
  streamTask(
    taskId: string,
    onFrame: (frame: HarnWireFrame) => void,
    onClose?: (code: number, reason: string) => void,
    onError?: (err: Error) => void,
  ): () => void {
    if (this.target.mode !== "direct") {
      throw new Error(
        "WS streaming not supported in gateway mode — openclaw plugin proxy returns 501 on upgrade. Use direct mode or poll GET /tasks/:id.",
      );
    }
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/harnws-direct/ws/tasks/${encodeURIComponent(taskId)}`;
    const ws = new WebSocket(url);

    ws.addEventListener("message", (ev) => {
      try {
        const frame = JSON.parse(typeof ev.data === "string" ? ev.data : "") as HarnWireFrame;
        onFrame(frame);
      } catch {
        // ignore malformed
      }
    });
    ws.addEventListener("close", (ev) => onClose?.(ev.code, ev.reason));
    ws.addEventListener("error", () => onError?.(new Error("WebSocket error")));

    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }
}
